<template>
	<div
		id="resume-score-panel-anchor"
		:class="showResumeScorer ? 'flex' : 'hidden'"
		class="
			fixed inset-x-0 top-[60px] bottom-0 z-60 w-full flex-col overflow-x-hidden overflow-y-auto border-l
			border-won-gray-light bg-white md:relative md:inset-auto md:z-auto md:max-w-[284px] md:shrink-0"
	>
		<div class="flex min-h-10 items-center justify-between border-b border-won-gray-light py-2 pr-5 pl-4">
			<h3 class="text-[16px] font-medium text-won-black">
				Resume Score
			</h3>

			<span
				class="cursor-pointer"
				@click="scorerStore.hideResumeScorer"
			>
				<PanelLeftClose />
			</span>
		</div>

		<div
			id="resume-score-target-anchor"
			class="border-b border-won-gray-light"
		>
			<!--
				EN-814: the role form reads its initial values once, so nothing here renders until this
				resume's score is in.
			-->
			<template v-if="hasCurrentScore">
				<div
					v-if="isEditingTarget"
					class="flex flex-col gap-2 bg-won-sand-light py-2 pr-5 pl-4"
				>
					<div
						v-if="!hasTargetSelected"
						class="flex flex-col gap-0.5"
					>
						<span class="text-[14px] font-medium text-won-black">Set Your Target Role</span>
						<span class="text-[12px] text-won-secondary">
							We couldn't detect a role to score this resume against. Set your target role and seniority to unlock your full score.
						</span>
					</div>

					<div
						v-else-if="alignmentStatus === 'needs_confirmation'"
						class="flex flex-col gap-0.5"
					>
						<span class="text-[14px] font-medium text-won-black">Set Your Target Role</span>
						<span class="text-[12px] text-won-secondary">
							We don't have an exact match for <span class="font-medium">{{ targetSelection.specialization }}</span>.
							Confirm the closest role we have, or pick another, to unlock your full score.
						</span>
					</div>

					<div
						v-else
						class="flex min-w-0 flex-col"
					>
						<span class="truncate text-[14px] font-medium text-won-black">{{ roleName }}</span>
						<span class="text-[12px] text-won-secondary">{{ levelName }} level</span>
					</div>

					<TargetSelection
						:key="formRevision"
						:resume-uuid="data.uuid"
						:initial-specialization="formInitialRole.specialization"
						:initial-title="formInitialRole.title"
						:initial-level="targetSelection?.level"
						:initial-track="targetSelection?.track"
						@selected="handleTargetSelected"
						@cancel="cancelEditingTarget"
					/>
				</div>

				<div
					v-else-if="!needsRoleInput"
					class="flex items-center gap-4 py-2 pr-5 pl-4"
				>
					<div class="flex min-w-0 flex-1 flex-col">
						<span class="truncate text-[14px] font-medium text-won-black">{{ roleName }}</span>
						<span class="text-[12px] text-won-secondary">{{ levelName }} level</span>
					</div>

					<button
						type="button"
						class="flex shrink-0 cursor-pointer items-center gap-1 text-[12px] font-medium text-won-green"
						@click="editingExistingTarget = true"
					>
						<PencilSquareIcon class="size-4" />
						Edit
					</button>
				</div>

				<div
					v-else
					class="flex items-center gap-1 py-2 pr-5 pl-4"
				>
					<svg
						class="size-5 shrink-0 text-[#CA8A04]"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="1.5"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
						/>
					</svg>

					<span class="flex-1 text-[14px] font-medium text-[#CA8A04]">Target role not set</span>

					<button
						type="button"
						class="flex shrink-0 cursor-pointer items-center gap-1 text-[12px] font-medium text-won-green"
						@click="targetFormDismissed = false"
					>
						<PencilSquareIcon class="size-4" />
						Edit
					</button>
				</div>
			</template>
		</div>

		<div class="flex flex-col items-center gap-2 p-4">
			<!-- EN-941: Figma's 80px ring (r = 37, 6px stroke); ScoreCircle insets r by a full stroke, hence 86. -->
			<ScoreCircle
				:progress="score?.totalScore || 0"
				:max-total="maxTotal"
				:pending="noScoreYet"
				:circle-width="86"
				:stroke-width="6"
				progress-class="text-heading-3 font-semibold!"
			/>

			<div
				v-if="pending"
				class="w-full rounded-md border border-won-gray-light bg-won-sand-light p-2 text-center"
			>
				<p class="text-[14px] font-medium text-won-black">No score yet</p>
				<p class="text-[12px] text-won-secondary">
					Fill in your education, or add your first real bullet point, to start scoring your resume and get recommendations.
				</p>
			</div>
		</div>

		<!-- EN-1039: re-keyed when a deep link resolves, so its category mounts expanded. -->
		<div
			:key="deepLinkedCategory ?? 'none'"
			class="flex w-full flex-col border-t border-won-gray-light"
		>
			<ScoreCategorySection
				title="Target Alignment"
				:has-items="alignmentStatus !== 'scored' || cards.alignment.length > 0"
				:current="score?.alignmentScore || 0"
				:max="40"
				default-open
				:disabled="pending"
				:locked-count="alignmentStatus === 'scored' ? lockedCounts.alignment : 0"
				@upgrade="triggerUpgrade"
			>
				<AlignmentLocked
					v-if="needsRoleInput || alignmentStatus === 'locked'"
					:role="needsRoleInput ? '' : targetSelection?.specialization"
				/>

				<AlignmentUnavailable
					v-else-if="alignmentStatus === 'unavailable'"
				/>

				<TransitionGroup
					v-else-if="alignmentStatus === 'scored'"
					v-bind="CARD_TRANSITION"
				>
					<ScoreIssueCard
						v-for="suggestion in cards.alignment"
						:key="suggestionKey(suggestion)"
						:data-suggestion-id="suggestion.id"
						:label="suggestion.label"
						:message="suggestion.message"
						:anchor="suggestion.anchor"
						:resolved="suggestion.resolved"
						:dot-color="CATEGORY_DOT_COLOR.alignment"
						@hover="warningHoverHandler"
						@leave="warningHoverResetHandler"
						@scroll-to="scrollTargetIntoView"
					/>
				</TransitionGroup>
			</ScoreCategorySection>

			<ScoreCategorySection
				title="Content Strength"
				:default-open="deepLinkedCategory === 'content'"
				:has-items="cards.content.length > 0"
				:current="score?.contentScore || 0"
				:max="30"
				:disabled="pending"
				:locked-count="lockedCounts.content"
				:empty-message="hasRealBullets ? null : NO_REAL_BULLETS"
				@upgrade="triggerUpgrade"
			>
				<TransitionGroup v-bind="CARD_TRANSITION">
					<ScoreIssueCard
						v-for="suggestion in cards.content"
						:key="suggestionKey(suggestion)"
						:data-suggestion-id="suggestion.id"
						:label="suggestion.label"
						:message="suggestion.message"
						:anchor="suggestion.anchor"
						:resolved="suggestion.resolved"
						:dot-color="CATEGORY_DOT_COLOR.content"
						:can-rewrite="suggestion.canRewrite"
						:rewrite-status="suggestion.rewriteStatus ?? null"
						@hover="warningHoverHandler"
						@leave="warningHoverResetHandler"
						@scroll-to="scrollTargetIntoView"
						@rewrite="rewriteCard(suggestion)"
						@accept="scorerStore.acceptRewrite(suggestion)"
						@undo="scorerStore.undoRewrite(suggestion)"
					/>
				</TransitionGroup>
			</ScoreCategorySection>

			<ScoreCategorySection
				title="Structure"
				:default-open="deepLinkedCategory === 'structure'"
				:has-items="cards.structure.length > 0"
				:current="score?.structureScore || 0"
				:max="20"
				:disabled="pending"
				:locked-count="lockedCounts.structure"
				@upgrade="triggerUpgrade"
			>
				<TransitionGroup v-bind="CARD_TRANSITION">
					<ScoreIssueCard
						v-for="suggestion in cards.structure"
						:key="suggestionKey(suggestion)"
						:data-suggestion-id="suggestion.id"
						:label="suggestion.label"
						:message="suggestion.message"
						:anchor="suggestion.anchor"
						:resolved="suggestion.resolved"
						:dot-color="CATEGORY_DOT_COLOR.structure"
						@hover="warningHoverHandler"
						@leave="warningHoverResetHandler"
						@scroll-to="scrollTargetIntoView"
					/>
				</TransitionGroup>
			</ScoreCategorySection>

			<ScoreCategorySection
				title="Completeness"
				:default-open="deepLinkedCategory === 'completeness'"
				:has-items="cards.completeness.length > 0"
				:current="score?.completenessScore || 0"
				:max="10"
				:disabled="pending"
				:locked-count="lockedCounts.completeness"
				@upgrade="triggerUpgrade"
			>
				<TransitionGroup v-bind="CARD_TRANSITION">
					<ScoreIssueCard
						v-for="suggestion in cards.completeness"
						:key="suggestionKey(suggestion)"
						:data-suggestion-id="suggestion.id"
						:label="suggestion.label"
						:message="suggestion.message"
						:anchor="suggestion.anchor"
						:resolved="suggestion.resolved"
						:dot-color="CATEGORY_DOT_COLOR.completeness"
						@hover="warningHoverHandler"
						@leave="warningHoverResetHandler"
						@scroll-to="scrollTargetIntoView"
					/>
				</TransitionGroup>
			</ScoreCategorySection>
		</div>

		<!-- EN-812: hidden while pending, when there is no score to break down. -->
		<button
			v-if="!pending"
			class="mx-4 my-4 flex cursor-pointer items-center justify-center rounded bg-won-green-light p-[9px] text-won-green"
			@click="scorerStore.setShowBreakdown"
		>
			View Score Breakdown
		</button>
	</div>
</template>

<script setup>
import { computed, nextTick, reactive, ref, onBeforeUnmount, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { toast } from 'vue-sonner';
import { useResumeScorerStore } from '../../store/scorer.store';
import { useBuilder } from '../../store/builder.store';
import { useSubscriptionPlanStore } from '../../store/subscription.store';
import { useFeatureUsageStore } from '../../store/feature-usage.store';
import { CATEGORY_DOT_COLOR, categoryOf, SUGGESTION_CATEGORIES, suggestionKey } from '../../utils/score-suggestions';
import { levelLabel } from '../../utils/target-selection';
import { useResolvingSuggestions } from '../../composables/useResolvingSuggestions';

import ScoreCircle from './score-circle.vue';
import ScoreCategorySection from './score-category-section.vue';
import ScoreIssueCard from './score-issue-card.vue';
import PanelLeftClose from '../../icons/panel-left-close.vue';
import PencilSquareIcon from '../../icons/pencil-square.vue';
import TargetSelection from './target-selection.vue';
import AlignmentLocked from './alignment-locked.vue';
import AlignmentUnavailable from './alignment-unavailable.vue';

// Tailwind's `md` starts at 768px — the same cutoff sidebar.store.js uses.
const MOBILE_MAX_WIDTH = 767;

// EN-812: Content Strength's prompt on a resume that can be scored but has no real bullets yet.
const NO_REAL_BULLETS = 'Add real bullet points to get content suggestions here.';

// EN-812: cards fade in and out; a fixed one shows its success state first (useResolvingSuggestions).
const CARD_TRANSITION = {
	tag: 'div',
	class: 'flex flex-col gap-1',
	enterActiveClass: 'transition-opacity duration-300',
	enterFromClass: 'opacity-0',
	leaveActiveClass: 'transition-opacity duration-300',
	leaveToClass: 'opacity-0',
};

const hoveredId = ref(null);
const scorerStore = useResumeScorerStore();
const subscriptionStore = useSubscriptionPlanStore();
const featuresUsageStore = useFeatureUsageStore();
const { isSubscribe } = storeToRefs(subscriptionStore);
const {
	getScore: score,
	showResumeScorer,
	maxTotal,
	alignmentStatus,
	getAlignment,
	suggestions,
	scoredResumeUuid,
	targetSelection,
	pending,
	noScoreYet,
	hasRealBullets,
	lockedCounts,
	getSuggestionsByCategory: suggestionsByCategory,
} = storeToRefs(scorerStore);
const builderStore = useBuilder();
const { data } = storeToRefs(builderStore);

// EN-812: each category's cards, keeping a just-fixed card on screen for its success state. A
// change of resume or target role replaces the cards without marking any fixed, as does the resume
// going pending: its cards leave because there is no score, not because they were fixed.
const scoreContext = computed(() => [
	scoredResumeUuid.value,
	targetSelection.value?.specialization,
	targetSelection.value?.level,
	targetSelection.value?.track,
	pending.value,
].join('|'));
const cards = reactive(Object.fromEntries(SUGGESTION_CATEGORIES.map((category) => {
	const { displayed } = useResolvingSuggestions(computed(() => suggestionsByCategory.value[category]), scoreContext);

	// EN-1146: a rewrite waiting for Accept or Undo stays on screen whatever the rescore says.
	return [category, computed(() => scorerStore.withRewrites(displayed.value, category))];
})));

const head = document.getElementsByTagName('head')[0];
const cssSheet = document.createElement('style');
head.appendChild(cssSheet);
cssSheet.type = 'text/css';

// EN-796: a resume has a real target once it's been scored against a
// selection (specialization/level/track), not just when the user has a
// job_title on their account. EN-802 removed v1, so the backend now always
// returns a v2-shaped payload — `scoreVersion` can no longer distinguish
// "target set" from "no target" the way it used to. `targetSelection` (the
// resolved specialization the payload was actually scored against, empty
// string when nothing resolved — see ResumeController::getScoreV2FromJobTitle)
// is the signal that still works.
const hasTargetSelected = computed(() => Boolean(targetSelection.value?.specialization));

const hasCurrentScore = computed(() => Boolean(data.value?.uuid) && scoredResumeUuid.value === data.value.uuid);

// EN-814: the panel still needs a role from the user when none was found, or
// when the one it has only loosely matches a profile (Medium confidence) and
// Target Alignment won't score until the user confirms one.
const needsRoleInput = computed(() => !hasTargetSelected.value || alignmentStatus.value === 'needs_confirmation');

// EN-814: a scored role shows the profile it was scored against; any other
// shows the title as the user or their profile gave it.
const roleName = computed(() => (alignmentStatus.value === 'scored' && getAlignment.value?.specialization)
	|| targetSelection.value?.specialization);
const levelName = computed(() => levelLabel(targetSelection.value?.level));

// EN-814: what the role form opens with. A scored role, or a Medium one's
// closest match, is a catalog pick; a role no profile covers is an "Other"
// title.
const formInitialRole = computed(() => {
	if (['scored', 'needs_confirmation'].includes(alignmentStatus.value)) {
		return { specialization: getAlignment.value?.specialization ?? null, title: null };
	}

	return { specialization: null, title: targetSelection.value?.specialization || null };
});

// EN-916: the target-role form shows inline (not as a floating dropdown) —
// by default while the panel still needs a role (EN-814), until the user
// cancels it (then it collapses to a compact "Target role not set" warning
// row with its own Edit link), or on demand when editing a role.
const targetFormDismissed = ref(false);
const editingExistingTarget = ref(false);
const isEditingTarget = computed(() => editingExistingTarget.value || (needsRoleInput.value && !targetFormDismissed.value));

const cancelEditingTarget = () => {
	editingExistingTarget.value = false;
	targetFormDismissed.value = needsRoleInput.value;
};

// The form already waited for the new score, so the panel shows the new role's
// treatment as soon as the form closes. A role that still needs input keeps the
// form open, so it is remounted: it reads its initial values only once, and must
// now open on what the server suggests (EN-814).
const formRevision = ref(0);

const handleTargetSelected = () => {
	targetFormDismissed.value = false;
	editingExistingTarget.value = false;
	formRevision.value += 1;
};

// EN-812: a category's "View N More Fixes" button opens the same upgrade
// prompt scorer-card.vue once used to gate the whole panel.
const triggerUpgrade = () => {
	featuresUsageStore.setShowLimitExceed('resume_scorer');
};

// EN-1146: Rewrite spends a bullet generation credit. A free account with none left gets the bullet
// generator's paywall (EN-811). A failed rewrite costs nothing, so the user can just try again.
const rewriteCard = async (suggestion) => {
	try {
		await scorerStore.rewriteSuggestion(suggestion);

		if (!isSubscribe.value) {
			featuresUsageStore.refresh().catch(() => {
				// Keep the cached count; it refreshes again after the next generation.
			});
		}
	} catch (error) {
		const status = error?.response?.status;

		if (status === 429) {
			featuresUsageStore.setShowLimitExceed('bullet_generation_tool');
		} else if (status === 409) {
			toast('This bullet is already fixed.');
			scorerStore.fetchScore();
		} else {
			toast.error('We couldn\'t rewrite this bullet. Try again in a moment.');
		}
	}
};

// EN-812: upgrading reveals every fix, so fetch them as soon as it lands.
watch(isSubscribe, (subscribed, wasSubscribed) => {
	if (subscribed && !wasSubscribed) {
		scorerStore.fetchScore();
	}
});

onBeforeUnmount(() => {
	cssSheet.remove();
});

const warningHoverHandler = (id) => {
	let css = '';
	const bp = document.getElementById(id);

	if (bp !== null) {
		if (id === 'pages-nr-exceeded') {
			css = `#${id} {
                    outline: 4px solid #FFD064;
                }`;
		} else {
			if (id.startsWith('highlight-')) {
				css = `#${id}>p:nth-of-type(1) {
                        background-color: #FFF0D0 !important;
                    }`;
			} else {
				css = `#${id}{
                        background-color: #FFF0D0 !important;
                    }`;
			}
		}
	}

	hoveredId.value = id;

	if (cssSheet.styleSheet) {
		cssSheet.styleSheet.cssText = css;
	} else {
		cssSheet.appendChild(document.createTextNode(css));
	}
};

const warningHoverResetHandler = () => {
	hoveredId.value = null;
	cssSheet.innerText = '';
};

// EN-798: click-to-scroll for alignment suggestions, mirroring the same
// behavior for v1 warnings.
const scrollTargetIntoView = (id) => {
	// EN-1067: below `md` the panel covers the resume, so close it to reveal the target.
	if (window.innerWidth <= MOBILE_MAX_WIDTH) {
		scorerStore.hideResumeScorer();
	}

	const target = document.getElementById(id);

	target?.scrollIntoView({ top: 20, behavior: 'smooth', block: 'start' });
};

// EN-1039: `?suggestion=<id>` (Your Journey's recommendation links) opens the panel on that card,
// with its category expanded. It waits for the linked resume's first score, because the card only
// exists once one arrives, then stops looking, matched or not, so a later rescore can't open the
// panel mid-edit. An id that score doesn't contain is ignored.
//
// On desktop it also highlights and scrolls to what the card points at. Below `md` the panel covers
// the resume, so it stays on the card, whose tap reveals the target (EN-1067). A card the account
// hasn't unlocked isn't in the score at all (EN-812), so it is never found.
const route = useRoute();
const deepLinkedCategory = ref(null);
let handledLink = null;

watch(suggestions, async (list) => {
	const link = route.query.suggestion ? `${route.params.id}|${route.query.suggestion}` : null;

	if (!link || link === handledLink || scoredResumeUuid.value !== route.params.id) {
		return;
	}

	handledLink = link;
	const suggestion = list.find((candidate) => candidate.id === route.query.suggestion);

	if (!suggestion) {
		return;
	}

	deepLinkedCategory.value = categoryOf(suggestion);
	scorerStore.setShowResumeScorer();
	await nextTick();

	document.querySelector(`[data-suggestion-id="${suggestion.id}"]`)?.scrollIntoView({ block: 'nearest' });

	if (suggestion.anchor && window.innerWidth > MOBILE_MAX_WIDTH) {
		warningHoverHandler(suggestion.anchor);
		scrollTargetIntoView(suggestion.anchor);
	}
});
</script>
